// image-input.component.ts
import {
  Component,
  EventEmitter,
  Output,
  Input,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { cn } from '../../../../utils/utils';
import { LoadingSpinnerComponent } from '../../loading-spinner/loading-spinner.component';
import { CommonModule } from '@angular/common';
import { ImageCropperModalComponent } from '../image-cropper-modal/image-cropper-modal.component';

let nextImageInputId = 0;

@Component({
  selector: 'image-input',
  templateUrl: './image-input.component.html',
  standalone: true,
  styleUrls: ['./image-input.component.css'],
  imports: [LoadingSpinnerComponent, CommonModule, ImageCropperModalComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageInputComponent implements OnChanges, OnDestroy {
  @Output() imageSelected = new EventEmitter<File | null>();
  @Output() imageDeleted = new EventEmitter<void>();
  @Input() previewUrl: string | null = null;
  @Input() isLoading: boolean = false;
  @Input() imageClass: string = '';
  @Input() imageContainerClass: string = '';
  @Input() labelClass: string = '';
  @Input() roundedClass: string = 'rounded-full';
  @Input() sizeClass: string = 'size-28';
  @Input() label: string = 'Upload Image';
  /**
   * What the image is, for screen readers ("Profile photo", "Organization
   * logo"). The file input is then named "Upload profile photo" or "Change
   * profile photo". Without it the visible label, or "image", is used.
   */
  @Input() ariaLabel: string = '';
  @Input() accept: string = 'image/*';
  /** Largest image that is uploaded. With the cropper on, this is checked on the cropped image. */
  @Input() maxsizeInMB: number = 2;
  /**
   * With the cropper on, the largest photo that can be chosen. Cropping redraws it as a
   * small PNG before upload, so a photo straight off a phone (often 3 to 8 MB) is fine.
   */
  @Input() maxSourceSizeInMB: number = 20;
  @Input() enableCropper: boolean = true;

  merge = cn;
  /** Unique per instance so two image inputs on one page never share a label target. */
  readonly inputId = `image-input-${nextImageInputId++}`;
  readonly hintId = `${this.inputId}-hint`;
  readonly errorId = `${this.inputId}-error`;

  errorMessage: string = '';
  showCropperModal: boolean = false;
  tempImageUrl: string = '';
  selectedFileName: string = '';
  /** Remove asks once before it deletes the saved image. */
  confirmingRemove = false;

  @ViewChild('fileInput') private fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('removeBtn') private removeBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('keepBtn') private keepBtn?: ElementRef<HTMLButtonElement>;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    // A new or cleared image ends any pending "Remove?" question.
    if (changes['previewUrl']) this.confirmingRemove = false;
  }

  /** The largest file that can be chosen. */
  get sourceLimitInMB(): number {
    return this.enableCropper ? Math.max(this.maxSourceSizeInMB, this.maxsizeInMB) : this.maxsizeInMB;
  }

  /** The image in words, lower-cased for use mid-sentence. */
  get subject(): string {
    const what = (this.ariaLabel || '').trim();
    return what ? what.charAt(0).toLowerCase() + what.slice(1) : 'image';
  }

  /** Accessible name of the file input, e.g. "Change profile photo". */
  get fileInputLabel(): string {
    const action = this.previewUrl ? 'Change' : 'Upload';
    if ((this.ariaLabel || '').trim()) return `${action} ${this.subject}`;
    return (this.label || '').trim() || `${action} image`;
  }

  /** While loading the input stays focusable but does not open the picker. */
  onFileInputClick(event: Event): void {
    if (this.isLoading) event.preventDefault();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate file type
      if (!file.type.match('image.*')) {
        this.setErrorMessage('That file is not an image. Choose a PNG, JPG or other image file.');
        input.value = ''; // let the same file be chosen again after the error
        return;
      }

      // Validate file size: the file itself, or with the cropper the photo it starts from
      const limitInMB = this.sourceLimitInMB;
      if (file.size > limitInMB * 1024 * 1024) {
        this.setErrorMessage(`That image is larger than ${limitInMB} MB. Choose a smaller file.`);
        input.value = ''; // let the same file be chosen again after the error
        return;
      }

      this.errorMessage = '';
      this.selectedFileName = file.name;

      // If cropper is enabled, show the cropper modal. An object URL, not a data URL:
      // a large photo is not copied into a string first.
      if (this.enableCropper) {
        this.releaseTempImage();
        this.tempImageUrl = URL.createObjectURL(file);
        this.showCropperModal = true;
        this.cdr.markForCheck();
      } else {
        // If cropper is disabled, emit the file directly
        this.imageSelected.emit(file);
      }

      // Reset file input to allow selecting the same file again
      input.value = '';
    }
  }

  onImageCropped(blob: Blob): void {
    // Only one save per open dialog, even if the cropper emits twice.
    if (!this.showCropperModal) return;

    // The cropped image is what gets uploaded, so it is the one held to the size limit.
    if (blob.size > this.maxsizeInMB * 1024 * 1024) {
      this.closeCropper();
      this.setErrorMessage(`The cropped image is larger than ${this.maxsizeInMB} MB. Zoom in and try again.`);
      return;
    }

    // Convert blob to file
    const file = new File([blob], this.selectedFileName || 'cropped-image.png', {
      type: blob.type,
    });

    // Close the modal
    this.closeCropper();

    // Use a micro-task to ensure the modal close is rendered before parent starts upload
    Promise.resolve().then(() => {
      this.imageSelected.emit(file);
    });
  }

  onCropperCancelled(): void {
    this.closeCropper();
    this.selectedFileName = '';
  }

  /** The browser could not open the file, e.g. a HEIC photo outside Safari. */
  onCropperFailed(): void {
    this.closeCropper();
    this.selectedFileName = '';
    this.setErrorMessage('This browser cannot open that image. Choose a JPG or PNG instead.');
  }

  private closeCropper(): void {
    this.showCropperModal = false;
    this.releaseTempImage();
    this.cdr.markForCheck();
  }

  private releaseTempImage(): void {
    if (this.tempImageUrl.startsWith('blob:')) URL.revokeObjectURL(this.tempImageUrl);
    this.tempImageUrl = '';
  }

  /**
   * The error stays until the next file is chosen or the image is removed,
   * so it never disappears before it has been read.
   */
  private setErrorMessage(message: string): void {
    this.errorMessage = message;
    this.cdr.markForCheck();
  }

  /** First Remove click: ask, and move focus to the safe answer. */
  askRemove(): void {
    this.confirmingRemove = true;
    this.cdr.detectChanges();
    this.keepBtn?.nativeElement.focus();
  }

  /** "Keep": back to Change / Remove, focus on Remove again. */
  keepImage(): void {
    this.confirmingRemove = false;
    this.cdr.detectChanges();
    this.removeBtn?.nativeElement.focus();
  }

  /** Confirmed: the parent deletes the saved image. */
  clearSelection(): void {
    this.errorMessage = '';
    this.confirmingRemove = false;
    this.imageDeleted.emit();
    this.cdr.detectChanges();
    // The Remove buttons are gone; keep focus in the control, on the Upload path.
    this.fileInput?.nativeElement.focus();
  }

  ngOnDestroy(): void {
    // Revoke blob URL if it exists to prevent memory leaks
    this.releaseTempImage();
  }
}
